import { useState, useEffect, useRef } from "react";
import "./Login.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe, faSpinner, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SUPPORTED_LANGUAGES } from "../translations.js";
import AuthVisual from "../components/AuthVisual.js";
import { saveInteraction } from "../components/Interaction.js";
import { returnToFromSearch, stashPendingReturnTo } from "../utils/session";
import { Alert, Button, TextField } from '@mui/material';
import { McpTheme } from '../components/McpUi';


function Login({ onLogin }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMcpLogin = returnToFromSearch(window.location.search) === '/mcp/authorize';
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // master login (owner only): the backend answers the master password with a code sent by e-mail
  const [challenge, setChallenge] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const currentLanguage = (i18n.language || "en").split("-")[0];
  const currentLangLabel = (SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || { label: "English" }).label;
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  useEffect(() => {
    if (!langOpen) return undefined;
    const onDown = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [langOpen]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const wixInstance = urlParams.get("instance");

    if (wixInstance) {
      const parts = wixInstance.split(".");
      if (parts.length === 3) {
        JSON.parse(atob(parts[1]));
      } else {
        console.warn("Invalid JWT format");
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.two_factor_required) {
          setChallenge(data.challenge_id);
          setSentTo(data.sent_to || "");
          return;
        }
        onLogin(true, data.token, data.user_id, data.first_name, data.language, data.date_format, data.timezone, data.role, data.owner_user_id, data.current_org_id);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || t("invalid_credentials"));
      }
    } catch (err) {
      setError(t("login_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}login/master-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge_id: challenge, code }),
      });
      if (response.ok) {
        const data = await response.json();
        onLogin(true, data.token, data.user_id, data.first_name, data.language, data.date_format, data.timezone, data.role, data.owner_user_id, data.current_org_id);
      } else {
        setError(t("login_master_code_invalid"));
      }
    } catch (err) {
      setError(t("login_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const language = localStorage.getItem("language") || "en";

    try {
      // Google OAuth leaves the SPA; park ?returnTo= so the ?token= return
      // leg (App.js) can land on the intended page (e.g. a live-chat deep
      // link) instead of the generic overview.
      stashPendingReturnTo(returnToFromSearch(window.location.search));
      if (!isMcpLogin) saveInteraction({ action: "google_login", recaptcha_score: 1 });
      window.location.href = `${process.env.REACT_APP_API_BASE_URL}login/google?lang=${language}`;
    } catch (err) {
      console.error("Login error", err);
      setError(t("login_error"));
    }
  };

  // A sign-up from here keeps the page the visitor was heading to (e.g. the Partner program); App.js decides
  // whether a brand-new account may land there.
  const handleRegisterRedirect = () => {
    if (isMcpLogin) stashPendingReturnTo('/mcp/authorize');
    const returnTo = returnToFromSearch(window.location.search);
    navigate(returnTo ? `/register?returnTo=${encodeURIComponent(returnTo)}` : '/register');
  };

  const handleForgotPassword = () => {
    if (isMcpLogin) stashPendingReturnTo('/mcp/authorize');
    navigate(isMcpLogin ? '/forgot-password?returnTo=%2Fmcp%2Fauthorize' : '/forgot-password');
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const page = (
    <div className="login-form-container">
      <a className="page-logo" href="/">
        <img src={"/ultimo-bots-white.png"} className="page-logo-img" alt="company_logo"></img>
      </a>
      <div className="login-lang" ref={langRef}>
        <button
          type="button"
          className="login-lang-trigger"
          onClick={() => setLangOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={langOpen}
        >
          <FontAwesomeIcon icon={faGlobe} className="login-lang-icon" />
          <span className="login-lang-current">{currentLangLabel}</span>
        </button>
        {langOpen && (
          <div className="login-lang-menu" role="listbox">
            {SUPPORTED_LANGUAGES.map((l) => {
              const sel = l.code === currentLanguage;
              return (
                <button
                  key={l.code}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  className={`login-lang-option ${sel ? "login-lang-sel" : ""}`}
                  onClick={() => { handleLanguageChange(l.code); setLangOpen(false); }}
                >
                  <span className="login-lang-check">{sel && <FontAwesomeIcon icon={faCheck} />}</span>{l.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="login-card">
        <div className="login-card-form">
      {challenge ? (
      <form className="login-form" onSubmit={handleVerify} autoComplete="off">
        <h1 className="form-title">{t("login_master_code_title")}</h1>
        <p className="form-subtitle">{t("login_master_code_sent", { email: sentTo })}</p>
        <label className="input-label" style={{marginBottom: '8px'}} htmlFor="master-code">{t("login_master_code_label")}</label>
        <div className="login-input">
          <input
            id="master-code"
            name="one-time-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            required
            autoFocus
          />
        </div>
        <div className="submit-area">
          <button className="submit-login" type="submit" disabled={loading || code.length !== 6}>
            {t("login_master_code_confirm")}
            {loading && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: '8px' }} />}
          </button>
        </div>
        {error && <div className="login-error">{error}</div>}
      </form>
      ) : (
      <form className="login-form" onSubmit={handleSubmit} autoComplete="on">
        <h1 className="form-title">{t(isMcpLogin ? 'mcp_signin_title' : 'login_welcome_back')}</h1>
        <p className="form-subtitle">{t(isMcpLogin ? 'mcp_signin_subtitle' : 'login_subtitle')}</p>

        <div className="login-buttons-container">
          <div className="google-login">
            <button
              type="button"
              className="google-login-btn"
              onClick={handleGoogleLogin}
            >
              <img
                src="../google-sign-in.png"
                alt={t("alt_google_logo")}
                className="google-logo"
              />
              {t("login_with_google")}
            </button>
          </div>
        </div>

        <div className="hr-sep"><span className="hr-text">{t("login_or_separator")}</span></div>

        {isMcpLogin ? <TextField fullWidth id="email" type="email" name="email" label={t('email')} value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" disabled={loading} sx={{ mb: 2 }} /> : <>
        <label className="input-label" style={{marginBottom: '8px'}} htmlFor="email">{t("email")}</label>
        <div className="login-input">
          <input
            id="email"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("login_email_placeholder")}
            required
            autoComplete="email"
          />
        </div>

        </>}
        {isMcpLogin ? <><TextField fullWidth id="password" type="password" name="password" label={t('password')} value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" disabled={loading} /><Button size="small" onClick={handleForgotPassword} sx={{ display: 'flex', ml: 'auto', mt: 1 }}>{t('login_forgot_password')}</Button></> : <>
        <div className="password-row">
          <label className="input-label" htmlFor="password">{t("password")}</label>
          <div className="forgot-link" onClick={handleForgotPassword}>{t("login_forgot_password")}</div>
        </div>
        <div className="login-input">
          <input
            id="password"
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("password")}
            required
            autoComplete="current-password"
          />
        </div>

        </>}
        <div className="submit-area">
          {isMcpLogin ? <Button variant="contained" fullWidth type="submit" disabled={loading}>{t(loading ? 'mcp_loading' : 'login')}</Button> :
          <button className="submit-login" type="submit" disabled={loading}>
            {t("login")}
            {loading && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: '8px' }} />}
          </button>}
        </div>

        {error && (isMcpLogin ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : <div className="login-error">{error}</div>)}

        <p className="terms-text">
          <Trans
            i18nKey="login_terms_agreement_html"
            components={{
              1: <a href="https://ultimo-bots.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>,
              3: <a href="https://ultimo-bots.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>,
            }}
          />
        </p>

        <div className="signup-row">
          <span>{t("login_no_account_prompt")}</span>
          <span className="signup-link" onClick={handleRegisterRedirect}>{t("login_sign_up_link")}</span>
        </div>
      </form>
      )}
        </div>
        <div className="login-card-visual"><AuthVisual /></div>
      </div>
    </div>
  );
  return isMcpLogin ? <McpTheme>{page}</McpTheme> : page;
}

export default Login;
